import { useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import type { MapDocument, MapLocation, MapRoute } from "../shared/types";
import type { VenueMapRouteData } from "./VenueMapRoute";
import routingWorkerSource from "virtual:event-map-routing-worker-source";
import { createInlineRoutingWorker } from "./inlineRoutingWorker";
import { createVenueMapRoutingClient } from "./venueMapRoutingClient";
import type {
  RoutingFailure,
  RoutingResult,
} from "./venueMapRoutingWorkerProtocol";

interface RouteRequest {
  document: MapDocument;
  from: MapLocation;
  to: MapLocation;
}
interface ResolvedRoute {
  request: RouteRequest;
  result: RoutingResult;
}
export interface AsyncVenueMapRoute {
  request: RouteRequest | null;
  status: "pending" | "ready" | "unavailable" | "cleared";
  route?: VenueMapRouteData;
}

export function useVenueMapRoute(
  document: MapDocument,
  requested: MapRoute | null | undefined,
  locations: ReadonlyMap<string, MapLocation>,
  onFailure?: (failure: RoutingFailure) => void,
): AsyncVenueMapRoute {
  const client = useMemo(
    () =>
      createVenueMapRoutingClient(() =>
        createInlineRoutingWorker(routingWorkerSource),
      ),
    [],
  );
  const failure = useRef(onFailure);
  failure.current = onFailure;
  const request = useMemo<RouteRequest | null>(() => {
    if (!requested) return null;
    const from = locations.get(requested.from);
    const to = locations.get(requested.to);
    return from && to ? { document, from, to } : null;
  }, [document, requested?.from, requested?.to, locations]);
  const [settled, setSettled] = useState<ResolvedRoute | null>(null);
  useLayoutEffect(() => {
    if (!request) {
      client.cancel();
      setSettled(null);
      return;
    }
    let current = true;
    void client
      .request(request.document, request.from.id, request.to.id)
      .then((result) => {
        if (!current || result.status === "cancelled") return;
        setSettled({ request, result });
        if (result.status === "unavailable" && result.failure)
          failure.current?.(result.failure);
      });
    return () => {
      current = false;
    };
  }, [client, request]);
  useLayoutEffect(() => () => client.dispose(), [client]);

  // A new prop value hides the old line immediately, before the effect runs.
  // It must not emit a misleading unavailable event while planning is pending.
  return useMemo(() => {
    if (!requested) return { request, status: "cleared" };
    if (!request) return { request, status: "unavailable" };
    if (settled?.request !== request) return { request, status: "pending" };
    const { result } = settled;
    if (result.status !== "ready") return { request, status: "unavailable" };
    return {
      request,
      status: "ready",
      route: {
        points: result.points,
        segments: result.segments,
        fromStopId: request.from.id,
        toStopId: request.to.id,
        geometry: document.geometry,
      },
    };
  }, [request, requested, settled, document.geometry]);
}
